import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Download, Eye, MoreHorizontal, ChevronDown, ChevronUp, ArrowUpDown } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import DocumentThumbnail from './ThumbnailComponent';
import type { SearchResultItem } from '@/types/search';


// Column type definitions
export interface ColumnDef {
  key: string;
  label: string;
  width: number;
  sortable?: boolean;
  hidden?: boolean;
  render?: (item: SearchResultItem) => React.ReactNode;
}

// Component props
interface DocumentsGridProps {
  documents: SearchResultItem[];
  columns?: ColumnDef[];
  loading?: boolean;
  onDocumentClick?: (id: string) => void;
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
  onDownload?: (documentId: string) => void;
  onViewDetails?: (documentId: string) => void;
  onDelete?: (documentId: string) => void;
  onShare?: (documentId: string) => void;
}

// Column Header component - Memoized to prevent unnecessary re-renders
const ColumnHeader = React.memo(({
  column,
  sortField,
  sortDirection,
  onSort,
  onResizeStart
}: {
  column: ColumnDef;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  onSort: (field: string) => void;
  onResizeStart: (e: React.MouseEvent) => void;
}) => {
  const isSorted = sortField === column.key;

  const handleClick = useCallback(() => {
    if (column.sortable) {
      onSort(column.key);
    }
  }, [column.key, column.sortable, onSort]);

  return (
    <div className="flex items-center justify-between group relative h-full">
      <Button
        variant="ghost"
        size="sm"
        className="h-8 -ml-3 font-medium"
        onClick={handleClick}
        disabled={!column.sortable}
      >
        {column.label}
        {column.sortable && (
          <span className="ml-2">
            {isSorted ? (
              sortDirection === 'asc' ?
                <ChevronUp className="h-4 w-4" /> :
                <ChevronDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4 opacity-50" />
            )}
          </span>
        )}
      </Button>

      <div
        className="absolute right-0 top-0 h-full w-1 cursor-col-resize opacity-0 group-hover:opacity-100 bg-gray-300"
        onMouseDown={onResizeStart}
      />
    </div>
  );
});

ColumnHeader.displayName = 'ColumnHeader';

export default function DocumentsGrid({
  documents,
  columns: initialColumns,
  loading = false,
  onDocumentClick,
  onSort,
  onDownload,
  onDelete,
  onShare
}: DocumentsGridProps) {
  // Refs for resize handling
  const tableRef = useRef<HTMLDivElement>(null);
  const resizingRef = useRef<{
    column: string;
    startX: number;
    startWidth: number;
  } | null>(null);

  // State
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [localDocuments, setLocalDocuments] = useState<SearchResultItem[]>(documents);
  
  useEffect(() => {
  setLocalDocuments(documents);
}, [documents]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      const response = await fetch(`/api/v1/documents/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete document');

      console.log('Deleted document:', id);

      setLocalDocuments(prev => prev.filter(doc => doc.document_id !== id));

      onDelete?.(id);

    } catch (error) {
      console.error('Error deleting document:', error);
    }
  }, [onDelete]);


  // Default columns configuration
  const defaultColumns: ColumnDef[] = useMemo(() => [
    {
      key: 'title',
      label: 'Title',
      width: 300,
      sortable: true,
      render: (item) => (
        <div className="flex items-center gap-3">
          <DocumentThumbnail
            thumbnail_url={item.thumbnail_url}
            title={item.title}
            hasThumbnail={!!item.thumbnail_url}
            category={item.category}
            size="sm"
          />
          <div>
            <div className="font-medium">{item.title}</div>
            <div className="text-sm text-gray-500">{item.author}</div>
          </div>
        </div>
      )
    },
    {
      key: 'date',
      label: 'Date',
      width: 150,
      sortable: true
    },
    {
      key: 'organization',
      label: 'Organization',
      width: 200,
      sortable: true
    },
    {
      key: 'category',
      label: 'Category',
      width: 150,
      sortable: true,
      render: (item) => (
        <Badge variant="outline" className="capitalize">
          {item.category}
        </Badge>
      )
    },
    {
      key: 'content_type',
      label: 'Content Type',
      width: 150,
      sortable: true,
      render: (item) => (
        <Badge variant="outline" className="capitalize">
          {item.classification?.content?.content_type || 'Unknown'}
        </Badge>
      )
    }
  ], []);

  const columns = initialColumns || defaultColumns;

  // Initialize column widths - only run when columns change
  useEffect(() => {
    const initialWidths: Record<string, number> = {};
    columns.forEach(col => {
      initialWidths[col.key] = col.width;
    });
    setColumnWidths(initialWidths);

    // Cleanup function
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [columns]);

  // Handle column resize - important to properly handle the event listeners
  const handleResizeStart = useCallback((columnKey: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling

    const currentWidth = columnWidths[columnKey] ||
      columns.find(col => col.key === columnKey)?.width || 100;

    resizingRef.current = {
      column: columnKey,
      startX: e.clientX,
      startWidth: currentWidth
    };

    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  }, [columns, columnWidths]);

  // Define handleResizeMove with no dependencies to avoid infinite loops
  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!resizingRef.current) return;

    const { column, startX, startWidth } = resizingRef.current;
    const difference = e.clientX - startX;
    const newWidth = Math.max(100, startWidth + difference);

    setColumnWidths(prev => ({
      ...prev,
      [column]: newWidth
    }));
  }, []);

  // HandleResizeEnd with no dependencies
  const handleResizeEnd = useCallback(() => {
    resizingRef.current = null;
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, []);

  // Handle sorting - Memoize to prevent recreating on every render
  const handleSort = useCallback((field: string) => {
    setSortConfig(current => ({
      field,
      direction: current?.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));

    // If parent component provided onSort callback
    if (onSort) {
      const direction = sortConfig?.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc';
      onSort(field, direction);
    }
  }, [sortConfig, onSort]);

  // Sort documents - Memoized to prevent recalculation on every render
  const sortedDocuments = useMemo(() => {
    if (!sortConfig) return localDocuments;

    return [...localDocuments].sort((a, b) => {
      let aValue = a[sortConfig.field as keyof SearchResultItem];
      let bValue = b[sortConfig.field as keyof SearchResultItem];

      // Handle nested properties for classification
      if (sortConfig.field === 'content_type') {
        aValue = a.classification?.content?.content_type;
        bValue = b.classification?.content?.content_type;
      }

      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;

      const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [localDocuments, sortConfig]);

  // Memoize table columns to prevent unnecessary re-renders
  const tableColumns = useMemo(() => {
    return columns.map((column) => (
      <TableHead
        key={column.key}
        style={{ width: columnWidths[column.key] }}
      >
        <ColumnHeader
          column={column}
          sortField={sortConfig?.field}
          sortDirection={sortConfig?.direction}
          onSort={handleSort}
          onResizeStart={(e) => handleResizeStart(column.key, e)}
        />
      </TableHead>
    ));
  }, [columns, columnWidths, sortConfig, handleSort, handleResizeStart]);

  // Memoize table rows to prevent unnecessary re-renders
  const tableRows = useMemo(() => {
    return sortedDocuments.map((doc) => (
      <TableRow
        key={doc.document_id}
        className="cursor-pointer hover:bg-gray-50"
        onClick={() => onDocumentClick?.(doc.document_id)}
      >
        {columns.map((column) => (
          <TableCell key={column.key}>
            {column.render ? column.render(doc) : doc[column.key as keyof SearchResultItem]}
          </TableCell>
        ))}
        <TableCell>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={(e) => {
                e.stopPropagation();
                onDownload?.(doc.document_id);
              }}
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={(e) => {
                e.stopPropagation();
                onDocumentClick?.(doc.document_id);
              }}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDocumentClick?.(doc.document_id); // reuse or update if needed
                  }}
                >
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(doc.document_id);
                  }}
                >
                  Delete
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownload?.(doc.document_id);
                  }}
                >
                  Download
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onShare?.(doc.document_id);
                  }}
                >
                  Share
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TableCell>
      </TableRow>
    ));
  }, [sortedDocuments, columns, onDocumentClick, onDownload]);

  // Loading state
  if (loading) {
    return (
      <div className="w-full h-64 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Render optimized table
  return (
    <div ref={tableRef} className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            {tableColumns}
            <TableHead className="w-20">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tableRows}
        </TableBody>
      </Table>
    </div>

  );
}